import React, { useEffect, useRef, useState } from "react";
import { api, formatCHF, formatNum, currentMonthString, formatMonat } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2, Upload, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";

const RATE_EMPTY = {
  tournummer: "",
  bezeichnung: "",
  kunde: "",
  umsatz_kunde: 0,
  standard_fahrerlohn: 100,
  aktiv: true,
};

const BILLING_EMPTY = {
  monat: currentMonthString(),
  fahrer_id: "",
  fahrzeug_id: "",
  arbeitstage: 0,
  tour_counts: [],
  wartezeit_stunden: 0,
  wartezeit_rate: 27.0,
  abzuege: 0,
  fahrerlohn: 0,
  bemerkung: "",
  status: "offen",
  upload_id: null,
};

function calcUmsatz(b) {
  const tours = (b.tour_counts || []).reduce(
    (s, t) => s + (Number(t.count) || 0) * (Number(t.rate) || 0),
    0
  );
  const wartezeit = (Number(b.wartezeit_stunden) || 0) * (Number(b.wartezeit_rate) || 0);
  return tours + wartezeit - (Number(b.abzuege) || 0);
}

export default function FTTouren() {
  const [tab, setTab] = useState("monatsabrechnung");
  const [monat, setMonat] = useState(currentMonthString());
  const [billings, setBillings] = useState([]);
  const [rates, setRates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  // billing dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BILLING_EMPTY);

  // tour rate dialog
  const [rateOpen, setRateOpen] = useState(false);
  const [rateEditing, setRateEditing] = useState(null);
  const [rateForm, setRateForm] = useState(RATE_EMPTY);

  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [b, r, e, v] = await Promise.all([
      api.get("/ft-monthly", { params: { monat } }),
      api.get("/tour-rates"),
      api.get("/employees"),
      api.get("/vehicles"),
    ]);
    setBillings(b.data);
    setRates(r.data);
    setEmployees(e.data);
    setVehicles(v.data);
  };
  useEffect(() => {
    load();
  }, [monat]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const vehKennz = (id) => vehicles.find((v) => v.id === id)?.kennzeichen || "—";
  const rateById = (id) => rates.find((r) => r.id === id);

  // ── Monatsabrechnung ──
  const openNew = () => {
    setEditing(null);
    setForm({ ...BILLING_EMPTY, monat });
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      ...BILLING_EMPTY,
      ...b,
      fahrzeug_id: b.fahrzeug_id || "",
      tour_counts: (b.tour_counts || []).map((t) => ({ ...t })),
    });
    setOpen(true);
  };

  const updateTourCount = (idx, patch) => {
    setForm((f) => ({
      ...f,
      tour_counts: f.tour_counts.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const addTourRow = () => {
    setForm((f) => ({
      ...f,
      tour_counts: [...(f.tour_counts || []), { tour_rate_id: "", bezeichnung: "", rate: 0, count: 0 }],
    }));
  };

  const removeTourRow = (idx) => {
    setForm((f) => ({
      ...f,
      tour_counts: f.tour_counts.filter((_, i) => i !== idx),
    }));
  };

  const pickTourRate = (idx, tourRateId) => {
    const tr = rateById(tourRateId);
    updateTourCount(idx, {
      tour_rate_id: tourRateId,
      bezeichnung: tr?.bezeichnung || "",
      rate: tr?.umsatz_kunde || 0,
    });
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        fahrer_id: form.fahrer_id || null,
        fahrzeug_id: form.fahrzeug_id || null,
        arbeitstage: Number(form.arbeitstage) || 0,
        wartezeit_stunden: Number(form.wartezeit_stunden) || 0,
        wartezeit_rate: Number(form.wartezeit_rate) || 27,
        abzuege: Number(form.abzuege) || 0,
        fahrerlohn: Number(form.fahrerlohn) || 0,
        tour_counts: (form.tour_counts || [])
          .filter((t) => t.tour_rate_id || t.bezeichnung)
          .map((t) => ({
            tour_rate_id: t.tour_rate_id || null,
            bezeichnung: t.bezeichnung || "",
            rate: Number(t.rate) || 0,
            count: Number(t.count) || 0,
          })),
      };
      if (!payload.fahrer_id) {
        toast.error("Bitte Fahrer auswählen.");
        return;
      }
      if (editing) await api.put(`/ft-monthly/${editing.id}`, payload);
      else await api.post("/ft-monthly", payload);
      toast.success("FT-Monatsabrechnung gespeichert");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("FT-Monatsabrechnung wirklich löschen?")) return;
    await api.delete(`/ft-monthly/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ft-monthly/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = r.data.extracted || {};
      const matched = r.data.matched_tours || [];
      const suggested = r.data.suggested_employee;
      // Build form prefilled
      const tourCounts = matched.map((m) => ({
        tour_rate_id: m.tour_rate_id || "",
        bezeichnung: m.bezeichnung || "",
        rate: m.rate || 0,
        count: m.count || 0,
      }));
      setEditing(null);
      setForm({
        ...BILLING_EMPTY,
        monat: ex.monat || monat,
        fahrer_id: suggested?.employee_id || "",
        arbeitstage: ex.arbeitstage || 0,
        tour_counts: tourCounts,
        wartezeit_stunden: ex.wartezeit_stunden || 0,
        wartezeit_rate: ex.wartezeit_rate || 27.0,
        abzuege: ex.abzuege || 0,
        upload_id: r.data.upload?.id,
      });
      setOpen(true);
      const unmatchedCount = matched.filter((m) => !m.tour_rate_id).length;
      if (unmatchedCount > 0) {
        toast.warning(`OCR erkannt — ${unmatchedCount} Tour(en) konnten nicht eindeutig zugeordnet werden, bitte manuell wählen.`);
      } else {
        toast.success("OCR erfolgreich — bitte Fahrer prüfen und speichern.");
      }
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ── Tour-Typen Stammdaten ──
  const openNewRate = () => {
    setRateEditing(null);
    setRateForm(RATE_EMPTY);
    setRateOpen(true);
  };
  const openEditRate = (r) => {
    setRateEditing(r);
    setRateForm({ ...RATE_EMPTY, ...r });
    setRateOpen(true);
  };
  const saveRate = async () => {
    try {
      const payload = {
        ...rateForm,
        umsatz_kunde: Number(rateForm.umsatz_kunde) || 0,
        standard_fahrerlohn: Number(rateForm.standard_fahrerlohn) || 0,
      };
      if (rateEditing) await api.put(`/tour-rates/${rateEditing.id}`, payload);
      else await api.post("/tour-rates", payload);
      toast.success("Tour-Typ gespeichert");
      setRateOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };
  const removeRate = async (id) => {
    if (!window.confirm("Tour-Typ wirklich löschen?")) return;
    await api.delete(`/tour-rates/${id}`);
    toast.success("Gelöscht");
    load();
  };

  // ── totals ──
  const totalUmsatz = billings.reduce((s, b) => s + calcUmsatz(b), 0);
  const totalFahrerlohn = billings.reduce((s, b) => s + (Number(b.fahrerlohn) || 0), 0);
  const totalArbeitstage = billings.reduce((s, b) => s + (Number(b.arbeitstage) || 0), 0);

  return (
    <PageContainer>
      <PageHeader title="FT-Touren" subtitle="Monatsabrechnung pro Fahrer · Tour-Typen Stammdaten" testId="ft-page">
        <MonthPicker value={monat} onChange={setMonat} testId="ft-month-picker" />
        <input
          type="file"
          ref={fileRef}
          className="hidden"
          accept="image/*,application/pdf"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          data-testid="ft-upload-input"
        />
        <Button
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="ft-upload-button"
          className="border-neutral-300"
        >
          {uploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
          {uploading ? "OCR läuft…" : "Smart-Upload (Mail/PDF)"}
        </Button>
        <Button onClick={openNew} data-testid="ft-new-button" className="bg-[#0055FF] hover:bg-[#0040CC]">
          <Plus className="h-4 w-4 mr-2" /> Manuell erfassen
        </Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-100">
          <TabsTrigger value="monatsabrechnung" data-testid="ft-tab-monatsabrechnung">Monatsabrechnung</TabsTrigger>
          <TabsTrigger value="stammdaten" data-testid="ft-tab-stammdaten">Tour-Typen Stammdaten</TabsTrigger>
        </TabsList>

        {/* ─── Monatsabrechnung ─── */}
        <TabsContent value="monatsabrechnung">
          <Card className="border-neutral-200 bg-white p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Einträge</div>
                <div className="text-lg font-semibold nivara-num" data-testid="ft-total-count">{billings.length}</div>
              </div>
              <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Arbeitstage gesamt</div>
                <div className="text-lg font-semibold nivara-num" data-testid="ft-total-days">{totalArbeitstage}</div>
              </div>
              <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Total Umsatz</div>
                <div className="text-lg font-semibold nivara-num" data-testid="ft-total-umsatz">{formatCHF(totalUmsatz)}</div>
              </div>
              <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Total Fahrerlohn</div>
                <div className="text-lg font-semibold nivara-num" data-testid="ft-total-fahrerlohn">{formatCHF(totalFahrerlohn)}</div>
              </div>
            </div>
          </Card>

          <Card className="border-neutral-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="ft-billings-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fahrer</TableHead>
                    <TableHead>Fahrzeug</TableHead>
                    <TableHead className="text-right">Tage</TableHead>
                    <TableHead>Touren (Anzahl × Tarif)</TableHead>
                    <TableHead className="text-right">Wartezeit</TableHead>
                    <TableHead className="text-right">Abzüge</TableHead>
                    <TableHead className="text-right">Umsatz</TableHead>
                    <TableHead className="text-right">Fahrerlohn</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billings.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-neutral-500 py-8">Keine FT-Monatsabrechnung für {formatMonat(monat)}.</TableCell></TableRow>
                  )}
                  {billings.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{empName(b.fahrer_id)}</TableCell>
                      <TableCell className="font-mono">{b.fahrzeug_id ? vehKennz(b.fahrzeug_id) : "—"}</TableCell>
                      <TableCell className="text-right nivara-num">{b.arbeitstage || 0}</TableCell>
                      <TableCell className="text-xs">
                        {(b.tour_counts || []).map((t, i) => (
                          <div key={i} className="whitespace-nowrap">
                            <span className="font-mono">{t.count}×</span>{" "}
                            <span className="text-neutral-600">{t.bezeichnung}</span>{" "}
                            <span className="text-neutral-400">@ {formatCHF(t.rate)}</span>
                          </div>
                        ))}
                      </TableCell>
                      <TableCell className="text-right nivara-num text-xs">
                        {Number(b.wartezeit_stunden || 0).toFixed(2)} h<br/>
                        <span className="text-neutral-400">@ {formatCHF(b.wartezeit_rate || 27)}</span>
                      </TableCell>
                      <TableCell className="text-right nivara-num">{b.abzuege ? `– ${formatCHF(b.abzuege)}` : "—"}</TableCell>
                      <TableCell className="text-right nivara-num font-semibold">{formatCHF(b.umsatz_kunde)}</TableCell>
                      <TableCell className="text-right nivara-num">{formatCHF(b.fahrerlohn)}</TableCell>
                      <TableCell>
                        <Badge className={b.status === "abgerechnet" ? "bg-[#008A00] text-white" : b.status === "geprüft" ? "bg-[#0055FF] text-white" : "bg-[#FFB800] text-neutral-900"}>{b.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`ft-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(b.id)} data-testid={`ft-delete-${b.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ─── Stammdaten ─── */}
        <TabsContent value="stammdaten">
          <Card className="border-neutral-200 bg-white p-4 mb-4 flex items-center justify-between">
            <div className="text-sm text-neutral-600">Tour-Typen mit festem Tarif. Werden in der Monatsabrechnung als Auswahl angeboten.</div>
            <Button onClick={openNewRate} variant="outline" data-testid="ft-rate-new-button">
              <Plus className="h-4 w-4 mr-2" /> Neuer Tour-Typ
            </Button>
          </Card>

          <Card className="border-neutral-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="ft-rates-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tournummer</TableHead>
                    <TableHead>Bezeichnung</TableHead>
                    <TableHead>Kunde</TableHead>
                    <TableHead className="text-right">Tarif CHF</TableHead>
                    <TableHead className="text-right">Std. Fahrerlohn</TableHead>
                    <TableHead>Aktiv</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-8">Keine Tour-Typen.</TableCell></TableRow>
                  )}
                  {rates.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono font-medium">{r.tournummer}</TableCell>
                      <TableCell>{r.bezeichnung}</TableCell>
                      <TableCell>{r.kunde}</TableCell>
                      <TableCell className="text-right nivara-num">{formatCHF(r.umsatz_kunde)}</TableCell>
                      <TableCell className="text-right nivara-num">{formatCHF(r.standard_fahrerlohn)}</TableCell>
                      <TableCell>{r.aktiv ? <Badge className="bg-[#008A00] text-white">Ja</Badge> : <Badge variant="outline">Nein</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditRate(r)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => removeRate(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Monatsabrechnung Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ft-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "FT-Monatsabrechnung bearbeiten" : "FT-Monatsabrechnung erfassen"}</DialogTitle>
            <DialogDescription>
              Erfasse pro Monat und Fahrer die Anzahl Tage, Touren pro Typ, Wartezeit, Abzüge und den Fahrerlohn. Der Umsatz wird automatisch berechnet.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Monat</Label>
              <Input type="month" value={form.monat} onChange={(e) => setForm({ ...form, monat: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>Fahrer</Label>
              <Select value={form.fahrer_id || "_none"} onValueChange={(v) => setForm({ ...form, fahrer_id: v === "_none" ? "" : v })}>
                <SelectTrigger data-testid="ft-fahrer-select"><SelectValue placeholder="Fahrer wählen…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Wählen —</SelectItem>
                  {employees.filter((e) => e.aktiv).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Fahrzeug (optional)</Label>
              <Select value={form.fahrzeug_id || "_none"} onValueChange={(v) => setForm({ ...form, fahrzeug_id: v === "_none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Kein Fahrzeug —</SelectItem>
                  {vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Arbeitstage</Label>
              <Input type="number" step="1" value={form.arbeitstage} onChange={(e) => setForm({ ...form, arbeitstage: e.target.value })} className="font-mono" data-testid="ft-arbeitstage" />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["offen","geprüft","abgerechnet"].map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Touren-Counts */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm uppercase tracking-wider text-neutral-700">Touren (Anzahl × Tarif)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addTourRow} data-testid="ft-add-tour-row">
                <Plus className="h-3.5 w-3.5 mr-1" /> Tour hinzufügen
              </Button>
            </div>
            <div className="space-y-2">
              {(form.tour_counts || []).length === 0 && (
                <div className="text-xs text-neutral-500 italic">Noch keine Touren erfasst — bitte mind. eine Tour hinzufügen.</div>
              )}
              {(form.tour_counts || []).map((t, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7">
                    <Label className="text-xs">Tour-Typ</Label>
                    <Select
                      value={t.tour_rate_id || "_custom"}
                      onValueChange={(v) => v === "_custom" ? updateTourCount(idx, { tour_rate_id: "" }) : pickTourRate(idx, v)}
                    >
                      <SelectTrigger data-testid={`ft-tour-select-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {rates.filter((r) => r.aktiv).map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.tournummer} — {r.bezeichnung} · {formatCHF(r.umsatz_kunde)}
                          </SelectItem>
                        ))}
                        <SelectItem value="_custom">— Eigener Eintrag —</SelectItem>
                      </SelectContent>
                    </Select>
                    {!t.tour_rate_id && (
                      <Input
                        placeholder="Bezeichnung (frei)"
                        value={t.bezeichnung || ""}
                        onChange={(e) => updateTourCount(idx, { bezeichnung: e.target.value })}
                        className="mt-1 text-xs"
                      />
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Anzahl</Label>
                    <Input type="number" step="1" min="0" value={t.count} onChange={(e) => updateTourCount(idx, { count: e.target.value })} className="font-mono" data-testid={`ft-tour-count-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Tarif CHF</Label>
                    <Input type="number" step="0.01" value={t.rate} onChange={(e) => updateTourCount(idx, { rate: e.target.value })} className="font-mono" />
                  </div>
                  <div className="col-span-1 pb-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTourRow(idx)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Wartezeit + Abzüge + Lohn */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="space-y-1">
              <Label>Wartezeit (h)</Label>
              <Input type="number" step="0.01" value={form.wartezeit_stunden} onChange={(e) => setForm({ ...form, wartezeit_stunden: e.target.value })} className="font-mono" data-testid="ft-wartezeit-stunden" />
            </div>
            <div className="space-y-1">
              <Label>Tarif Wartezeit (CHF/h)</Label>
              <Input type="number" step="0.01" value={form.wartezeit_rate} onChange={(e) => setForm({ ...form, wartezeit_rate: e.target.value })} className="font-mono" data-testid="ft-wartezeit-rate" />
            </div>
            <div className="space-y-1">
              <Label>Abzüge (CHF)</Label>
              <Input type="number" step="0.01" value={form.abzuege} onChange={(e) => setForm({ ...form, abzuege: e.target.value })} className="font-mono" data-testid="ft-abzuege" />
            </div>
            <div className="space-y-1">
              <Label>Fahrerlohn (CHF)</Label>
              <Input type="number" step="0.01" value={form.fahrerlohn} onChange={(e) => setForm({ ...form, fahrerlohn: e.target.value })} className="font-mono" data-testid="ft-fahrerlohn" />
            </div>
          </div>

          <div className="space-y-1 mt-3">
            <Label>Bemerkung</Label>
            <Textarea value={form.bemerkung || ""} onChange={(e) => setForm({ ...form, bemerkung: e.target.value })} />
          </div>

          <Card className="bg-neutral-50 p-3 border-neutral-200 mt-3">
            <div className="text-xs uppercase tracking-[0.1em] text-neutral-500 mb-1">Berechneter Umsatz Kunde</div>
            <div className="text-xl font-semibold nivara-num" data-testid="ft-form-umsatz">{formatCHF(calcUmsatz(form))}</div>
          </Card>

          {form.upload_id && (
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" /> Originaldatei verknüpft (ID: {form.upload_id.slice(0, 8)}…)
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ft-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tour Rate Dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent data-testid="ft-rate-dialog">
          <DialogHeader>
            <DialogTitle>{rateEditing ? "Tour-Typ bearbeiten" : "Neuer Tour-Typ"}</DialogTitle>
            <DialogDescription>
              Pflege die festen FT-Tour-Typen mit Tarif. Diese werden in der Monatsabrechnung als Auswahl angeboten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tournummer</Label>
              <Input value={rateForm.tournummer} onChange={(e) => setRateForm({ ...rateForm, tournummer: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>Kunde</Label>
              <Input value={rateForm.kunde} onChange={(e) => setRateForm({ ...rateForm, kunde: e.target.value })} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Bezeichnung</Label>
              <Input value={rateForm.bezeichnung} onChange={(e) => setRateForm({ ...rateForm, bezeichnung: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Tarif Kunde (CHF)</Label>
              <Input type="number" step="0.01" value={rateForm.umsatz_kunde} onChange={(e) => setRateForm({ ...rateForm, umsatz_kunde: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>Std. Fahrerlohn (Empfehlung)</Label>
              <Input type="number" step="0.01" value={rateForm.standard_fahrerlohn} onChange={(e) => setRateForm({ ...rateForm, standard_fahrerlohn: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1 flex items-center gap-2 pt-6">
              <Checkbox checked={rateForm.aktiv} onCheckedChange={(v) => setRateForm({ ...rateForm, aktiv: !!v })} id="aktiv" />
              <Label htmlFor="aktiv">Aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateOpen(false)}>Abbrechen</Button>
            <Button onClick={saveRate} className="bg-[#0055FF] hover:bg-[#0040CC]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
